import { yupResolver } from '@hookform/resolvers/yup'
import { FC, ReactNode, useState } from 'react'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'

import closeBtn from '../../../../assets/images/close-btn.svg'
import { BUDGET_BASED } from '../../../../resources/constants'
import { SettingsBudgetI } from '../../../../resources/form-props'
import { SettingsBudgetValidate } from '../../../../resources/form-validator'
import { transformMonths } from '../../../../resources/functions'
import { useSettingsApi } from '../../../../resources/hooks/api/useSettingsApiHook'
import { getBranch } from '../../../../store/user/selectors'

interface Props {
  closeModal: () => void
  record: any
}

const SettingsAccountingModal: FC<Props> = ({ closeModal, record }) => {
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const months = transformMonths()

  const { updateAccountingSettings } = useSettingsApi()
  const { t } = useTranslation('components.Modal.Settings.accounting')

  const branchData = useSelector(getBranch)

  const {
    control,
    reset,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<SettingsBudgetI>({
    defaultValues: {
      first_month_of_fiscal_year: record?.first_month_of_fiscal_year || null,
      first_month_of_income_tax_year:
        record?.first_month_of_income_tax_year || null,
      budget_based_on: record?.budget_based_on || BUDGET_BASED[1],
    },
    resolver: yupResolver(SettingsBudgetValidate),
  })

  // ✅ Main logic
  const budgetBaseedOnUpdate = watch('budget_based_on')
  const isFiscalYear = budgetBaseedOnUpdate?.value === 2

  const processData = (data: any) => {
    const params = {
      budget_based_on: data.budget_based_on?.value || null,
      first_month_of_fiscal_year:
        data.first_month_of_fiscal_year?.value || null,
      first_month_of_income_tax_year:
        data.first_month_of_income_tax_year?.value || null,
    }

    setShowLoader(true)
    updateAccountingSettings(
      { params, branch_id: +branchData['id'] },
      (message: string) => {
        setShowLoader(false)
        toast.success(message)
        _handleCloseModal()
      },
      (message: string) => {
        setShowLoader(false)
        toast.error(message)
      }
    )
  }

  const _handleCloseModal = () => {
    reset()
    closeModal()
  }

  return (
    <Modal
      show={true}
      onHide={_handleCloseModal}
      backdrop="static"
      animation={false}
      size="lg"
    >
      <Form onSubmit={handleSubmit(processData)}>
        <div className="modal-header">
          <h5 className="modal-title">{t('modal.title')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={_handleCloseModal}
          >
            <img src={closeBtn} alt="" />
          </button>
        </div>

        <div className="modal-body pb-0">
          <div className="row">
            {/* Budget Based On */}
            <div className="col-sm-6">
              <div className="form-group">
                <label>{t('fields.budgetBasedOn')} *</label>

                <Controller
                  control={control}
                  name="budget_based_on"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t('placeholders.selectBudgetType')}
                      options={BUDGET_BASED}
                    />
                  )}
                />

                {errors?.budget_based_on && (
                  <Form.Text className="text-danger">
                    {errors.budget_based_on.message as ReactNode}
                  </Form.Text>
                )}
              </div>
            </div>

            {/* First Month of Calendar Year (Always Disabled) */}
            <div className="col-sm-6">
              <div className="form-group">
                <label>{t('fields.firstMonthCalendarYear')} *</label>

                <Controller
                  control={control}
                  name="first_month_of_fiscal_year"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t('placeholders.selectMonth')}
                      options={months}
                      isDisabled={true}
                    />
                  )}
                />

                {errors?.first_month_of_fiscal_year && (
                  <Form.Text className="text-danger">
                    {errors.first_month_of_fiscal_year.message as ReactNode}
                  </Form.Text>
                )}
              </div>
            </div>

            {/* First Month of Fiscal Year */}
            <div className="col-sm-6">
              <div className="form-group">
                <label>{t('fields.firstMonthFiscalYear')} *</label>

                <Controller
                  control={control}
                  name="first_month_of_income_tax_year"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t('placeholders.selectMonth')}
                      options={months}
                      isDisabled={!isFiscalYear}
                    />
                  )}
                />

                {errors?.first_month_of_income_tax_year && (
                  <Form.Text className="text-danger">
                    {errors.first_month_of_income_tax_year.message as ReactNode}
                  </Form.Text>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={_handleCloseModal}
          >
            {t('actions.close')}
          </button>

          <button type="submit" className="btn btn-primary">
            {showLoader ? <Spinner size="sm" /> : t('actions.saveChanges')}
          </button>
        </div>
      </Form>
    </Modal>
  )
}

export default SettingsAccountingModal
